import React, { Suspense, useMemo } from 'react';
import { Entry, Onlive, Optional } from '@2024-contest-web/components/src/types';
import classNames from 'classnames';
import { isMock } from '../../resources';
import { Photo } from '@2024-contest-web/components/src/components/Photo';
import { Footer } from '@2024-contest-web/components/src/components/Footer';
import { SectionTitle } from '@2024-contest-web/components/src/components/SectionTitle';
import { Entries } from '@2024-contest-web/components/src/components/Entries';
import { Header } from '@2024-contest-web/components/src/components/Header';
import { EntryCard } from '@2024-contest-web/components/src/components/EntryCard';
import {
  ShowroomButton,
  MystaButton,
  TiktokButton,
  TwitterButton,
  InstagramButton,
} from '@2024-contest-web/components/src/components/Button';
import { ProfileBreadCrumb } from '@2024-contest-web/components/src/components/BreadCrumb';
import { VoteButton } from '@2024-contest-web/components/src/components/VoteButton';
import { Helmet } from 'react-navi-helmet-async';
import { Link, useCurrentRoute } from 'react-navi';
import { useResource } from 'retil';
import { entryList, liveScheduleList, onliveList } from '../../resources';
import { useStageInfo } from '@2024-contest-web/components/src/hooks';
import dayjs from 'dayjs';
import ja from 'dayjs/locale/ja';
dayjs.locale(ja);

interface ProfileAppProps {
  entry: Entry;
}

export function ProfileApp({ entry }: ProfileAppProps) {
  return (
    <main className="ProfileApp">
      <Helmet>
        <title>
          {entry.name ?? ''} | {process.env.REACT_APP_WEBSITE_NAME}
        </title>
        <meta content={`${entry.name ?? ''} | ${process.env.REACT_APP_WEBSITE_NAME}`} property="og:title" />
        <meta content={`${entry.image ?? process.env.REACT_APP_URL + '/img/ogp.jpg'}`} property="og:image" />
        <meta content={`${process.env.REACT_APP_URL}/${entry.entry_id}`} property="og:url" />
      </Helmet>
      <Header />
      <section className="section as-profile">
        <div className="container">
          <ProfileBreadCrumb entry={entry} />
          <div className="columns is-variable is-8">
            <div className="column">
              {isMock() ? (
                <EntryPhotoFallback entry={entry} />
              ) : (
                <Suspense fallback={<EntryPhotoFallback entry={entry} />}>
                  <EntryPhoto entry={entry} />
                </Suspense>
              )}
            </div>
            <div className="column">
              <div className="columns is-size-3 is-variable is-multiline">
                <div className="column is-narrow">
                  <div className="columns">
                    <div className="column is-narrow" style={{ maxWidth: '100%' }}>
                      <h1>{entry.name}</h1>
                    </div>
                  </div>
                </div>
                <div className="column is-narrow is-padding-top-0-mobile">
                  <div className="columns is-mobile is-multiline is-variable is-2 is-wrap">
                    {entry.showroom && (
                      <div className="column is-narrow is-flex is-align-items-center">
                        <ShowroomButton
                          href={`https://www.showroom-live.com/room/profile?room_id=${entry.showroom}`}
                          target="_blank"
                        />
                      </div>
                    )}
                    {entry.mysta && (
                      <div className="column is-narrow is-flex is-align-items-center">
                        <MystaButton
                          href={`https://appweb.mysta.tv/web/share/profile?id=${entry.mysta}`}
                          target="_blank"
                        />
                      </div>
                    )}
                    {entry.instagram && (
                      <div className="column is-narrow">
                        <InstagramButton
                          href={`https://instagram.com/${entry.instagram}`}
                          target="_blank"
                          style={{ marginTop: 3 }}
                        />
                      </div>
                    )}
                    {entry.twitter && (
                      <div className="column is-narrow">
                        <TwitterButton
                          href={`https://twitter.com/${entry.twitter}`}
                          target="_blank"
                          style={{ marginTop: 3 }}
                        />
                      </div>
                    )}
                    {entry.tiktok && (
                      <div className="column is-narrow">
                        <TiktokButton
                          href={`https://tiktok.com/@${entry.tiktok}`}
                          target="_blank"
                          style={{ marginTop: 3 }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {!isMock() && (
                <Suspense fallback={null}>
                  <EntryLiveSchedules entry={entry} />
                </Suspense>
              )}
              <div className="is-margin-bottom-1">
                <p className="has-text-primary">大学</p>
                <p className="is-size-5">
                  {entry.university} {entry.grade && `${entry.grade}年`}
                </p>
              </div>
              <div className="is-margin-bottom-1">
                <p className="has-text-primary">出身地</p>
                <p className="is-size-5">{entry.hometown}</p>
              </div>
              <div className="is-margin-bottom-1">
                <p className="has-text-primary">誕生日</p>
                <p className="is-size-5">{entry.birthday}</p>
              </div>
              {entry.hobby && (
                <div className="is-margin-bottom-1">
                  <p className="has-text-grey">趣味</p>
                  <p className="is-size-5">{entry.hobby}</p>
                </div>
              )}
              {entry.skill && (
                <div className="is-margin-bottom-1">
                  <p className="has-text-grey">特技</p>
                  <p className="is-size-5">{entry.skill}</p>
                </div>
              )}
              {entry.dream && (
                <div className="is-margin-bottom-1">
                  <p className="has-text-grey">将来の夢</p>
                  <p className="is-size-5">{entry.dream}</p>
                </div>
              )}
              {entry.challenge && (
                <div className="is-margin-bottom-1">
                  <p className="has-text-grey">学生時代に挑戦したいこと</p>
                  <p className="is-size-5">{entry.challenge}</p>
                </div>
              )}
              {entry.ambition && (
                <div className="is-margin-bottom-1">
                  <p className="has-text-grey">意気込み</p>
                  <p className="is-size-5">{entry.ambition}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {!isMock() && (
        <Suspense fallback={null}>
          <EntryList entry={entry} />
        </Suspense>
      )}
      <Footer href="mailto:frecam@age.co.jp" />
    </main>
  );
}

function EntryPhotoFallback({ entry }: { entry: Entry }) {
  return (
    <a className="profile-image-link">
      <Photo className={classNames('is-solided')} src={entry.image ?? '/img/noimage.svg'} alt={entry.name ?? ''} />
    </a>
  );
}

function EntryPhoto({ entry }: { entry: Entry }) {
  const [onlives] = useResource(onliveList);
  const onlive: Optional<Onlive> = useMemo(() => {
    return onlives.data.filter(item => item.entry_id == entry.entry_id)[0];
  }, [onlives.data, entry.entry_id]);
  return (
    <a
      className="profile-image-link"
      target="_blank"
      href={onlive ? `https://www.showroom-live.com/${onlive.roomUrl}` : undefined}
    >
      <Photo
        className={classNames('is-solided', {
          'is-onlive': !!onlive,
        })}
        src={entry.image ?? '/img/noimage.svg'}
        alt={entry.name ?? ''}
      >
        <figcaption className="entry-card-mask">
          <figure className="image is-1by1">
            <svg width="240" height="240" viewBox="0 0 240 240">
              <path fill="none" d="M15,14.5 v210 h210" />
              <path fill="none" d="M225,225 v-210 h-210" />
            </svg>
            <figcaption>
              <p className="has-text-weight-bold">配信サイトを見る</p>
            </figcaption>
          </figure>
        </figcaption>
      </Photo>
    </a>
  );
}

function EntryLiveSchedules({ entry }: { entry: Entry }) {
  const [liveSchedules] = useResource(liveScheduleList, entry.showroom ?? '');
  if (liveSchedules.data.length === 0) {
    return null;
  }
  return (
    <div className="is-margin-bottom-1">
      <p className="has-text-primary">配信予定</p>
      {liveSchedules.data.map(schedule => (
        <p className="is-size-5" key={schedule.id}>
          <span style={{ borderBottom: '1px dashed #cccccc' }}>
            {dayjs(schedule.start).format('YYYY年M月D日(ddd) HH:mm')}〜
          </span>
        </p>
      ))}
      <p className="help">※配信者の都合で予告なく変更となる場合があります</p>
    </div>
  );
}
function EntryList({ entry }: { entry: Entry }) {
  const {
    url: { query },
  } = useCurrentRoute();
  const [list] = useResource(entryList);
  const [onlives] = useResource(onliveList);
  const { stagePath } = useStageInfo(entry.stage);

  return (
    <section id="entries" className="section has-background-white-ter">
      <div className="container">
        <SectionTitle className="title" style={{ marginBottom: 60 }} title={'Entries'} subtitle={'出場者紹介'} />
        <div className="columns is-multiline is-mobile is-margin-bottom-3">
          <Entries state={list} size={4} except={entry} stage={entry.stage}>
            {(list: Entry[]) =>
              list.map((item: Entry, index: number) => (
                <div
                  className={classNames('column is-3-desktop is-4-tablet is-6-mobile', {
                    'is-hidden-tablet-only': index === 3,
                  })}
                  key={item.entry_id}
                >
                  <EntryCard onlive={isOnlive(item)} entry={item} href={`/entry/${item.entry_id}`} />
                </div>
              ))
            }
          </Entries>
        </div>
        <div className="has-text-centered">
          <Link className="button is-rounded is-medium is-frecam" href={stagePath}>
            <span>もっと見る</span>
          </Link>
        </div>
      </div>
    </section>
  );

  function isOnlive(entry: Entry) {
    const [onlive] = onlives.data.filter((onlive: Onlive) => onlive.entry_id == entry.entry_id);
    return !!onlive;
  }
}
